/**
 * AGREEMENT CHECKER COMPONENT
 *
 * Global component that handles agreement acceptance modals
 * when triggered by API errors (403 AGREEMENT_REQUIRED).
 *
 * IMPORTANT: This component does NOT automatically check for pending
 * agreements on login. This is intentional - we don't want to interrupt
 * users who just want to browse. Agreements are only required when:
 * - User tries to checkout (buyer terms)
 * - User tries to onboard to Stripe (seller agreement)
 * - User tries to create a business (if required)
 *
 * The backend middleware blocks these actions and returns a 403 error,
 * which the Axios interceptor catches and triggers this modal.
 *
 * USAGE:
 *   import AgreementChecker from './components/agreements/AgreementChecker';
 *
 *   function App() {
 *     return (
 *       <>
 *         <Routes>...</Routes>
 *         <AgreementChecker />
 *       </>
 *     );
 *   }
 *
 * CREATED: July 2025
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AgreementAcceptanceModal from './AgreementAcceptanceModal';
import {
  setupAgreementInterceptor,
  registerAgreementCallbacks,
  unregisterAgreementCallbacks
} from '../../utils/agreementInterceptor';

// Pages where we should NOT show the agreement modal
// (to avoid blocking critical flows)
const EXCLUDED_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/logout'
];

// Setup interceptor once on module load
let interceptorSetup = false;
if (!interceptorSetup) {
  setupAgreementInterceptor();
  interceptorSetup = true;
}

const AgreementChecker = () => {
  const [pendingAgreements, setPendingAgreements] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const { data: user } = useSelector((state) => state.user);
  const location = useLocation();

  // Check if current path is excluded
  const isExcludedPath = EXCLUDED_PATHS.some(path =>
    location.pathname.startsWith(path)
  );

  // Register callbacks with the interceptor
  // This allows the interceptor to trigger the modal when an API returns AGREEMENT_REQUIRED
  useEffect(() => {
    registerAgreementCallbacks(setShowModal, setPendingAgreements);

    return () => {
      unregisterAgreementCallbacks();
    };
  }, []);

  // Clear state when user logs out
  useEffect(() => {
    if (!user) {
      setPendingAgreements([]);
      setShowModal(false);
    }
  }, [user]);

  // Handle successful acceptance
  const handleAccepted = useCallback(() => {
    setShowModal(false);
    setPendingAgreements([]);
    // User can now retry their action (checkout, etc.)
  }, []);

  // Handle closing without accepting
  // User can dismiss and continue browsing, but won't be able to
  // complete the protected action (checkout, etc.) until they accept
  const handleClose = useCallback(() => {
    setShowModal(false);
    setPendingAgreements([]);
    // User returns to their current page, action is cancelled
  }, []);

  // Don't render if no pending agreements or on excluded paths
  if (!showModal || pendingAgreements.length === 0 || isExcludedPath) {
    return null;
  }

  return (
    <AgreementAcceptanceModal
      isOpen={showModal}
      agreements={pendingAgreements}
      onAccept={handleAccepted}
      onClose={handleClose}
      allowClose={true}
      title="Please Accept Our Terms"
    />
  );
};

export default AgreementChecker;
